import { Alert, Button, Card, Checkbox, Input, Spin, Tooltip, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import "./PrivacyPolicyForm.css";
import {
  getManagedPrivacyPolicy,
  publishManagedPrivacyPolicy,
  saveManagedPrivacyPolicy,
} from "src/services/privacyPolicyService";
import { getFromLocal } from "src/util/cache.service";
import { LOCAL_KEYS, USER_ROLE } from "src/util/util.constant";

const { Text, Title } = Typography;

const normalize = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

interface UserDetail {
  role?: unknown;
  roles?: unknown;
  userRole?: { name?: unknown };
}

const canManage = () => {
  const role = getFromLocal<unknown>(LOCAL_KEYS.ROLE, false, true);
  const detail = getFromLocal<UserDetail>(LOCAL_KEYS.USER_DETAIL, true, true);
  const roles = [role, detail?.role, detail?.userRole?.name, detail?.roles].flat();

  return roles.some((currentRole) =>
    [USER_ROLE.RootrUser, USER_ROLE.SuperUser].some(
      (allowedRole) => normalize(currentRole) === normalize(allowedRole)
    )
  );
};

export interface TocItem {
  id: string;
  label: string;
  level: number;
}

const slug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s+/g, "-") || "section";

const extractHeadings = (markdown: string): TocItem[] => {
  const used = new Map<string, number>();
  let code = false;

  return markdown.split(/\r?\n/).flatMap((line) => {
    if (line.trim().startsWith("```")) {
      code = !code;
      return [];
    }
    if (code) return [];

    const match = /^(#{1,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match) return [];

    const label = (match[2] ?? "").trim();
    const base = slug(label);
    const count = used.get(base) ?? 0;
    used.set(base, count + 1);

    return [
      {
        id: count ? `${base}-${count + 1}` : base,
        label,
        level: (match[1] ?? "#").length,
      },
    ];
  });
};

export const PrivacyPolicyPreview = ({
  markdown,
  showIndex = false,
}: {
  markdown: string;
  showIndex?: boolean;
}) => {
  const { t } = useTranslation();
  const headings = extractHeadings(markdown);
  let headingIndex = 0;

  const getHeadingId = () => headings[headingIndex++]?.id;
  const components: Components = {
    h1: ({ children }) => <h1 id={getHeadingId()}>{children}</h1>,
    h2: ({ children }) => <h2 id={getHeadingId()}>{children}</h2>,
    h3: ({ children }) => <h3 id={getHeadingId()}>{children}</h3>,
  };

  return (
    <>
      {showIndex && (
        <nav aria-label="Table of contents" className="privacy-policy-index mb-6 rounded p-4">
          <Text strong>{t("privacy_policy_index")}</Text>
          <ul>
            {headings.map((heading) => (
              <li key={heading.id} style={{ marginLeft: (heading.level - 1) * 14 }}>
                <a href={`#${heading.id}`} className="privacy-policy-link">
                  {heading.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <div className="privacy-policy-preview">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {markdown}
        </ReactMarkdown>
      </div>
    </>
  );
};

const PrivacyPolicyForm = () => {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState("# Privacy Policy\n\nWrite the privacy policy here.");
  const [savedMarkdown, setSavedMarkdown] = useState("");
  const [preview, setPreview] = useState(markdown);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    getManagedPrivacyPolicy()
      .then((value) => {
        if (!active) return;
        const content = value?.content ?? "";
        setMarkdown(content);
        setSavedMarkdown(content);
        setPreview(content);
      })
      .catch(() => active && setError(t("privacy_policy_load_error")))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [t]);

  const hasUnsavedChanges = markdown !== savedMarkdown;
  const canSave = confirmed && hasUnsavedChanges && markdown === preview;
  const confirmationTooltip = !hasUnsavedChanges
    ? t("privacy_policy_confirm_disabled_no_changes")
    : markdown !== preview
      ? t("privacy_policy_confirm_disabled_no_preview")
      : undefined;

  if (!canManage()) return <Navigate to="/" replace />;
  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spin tip={t("privacy_policy_saving")} />
      </div>
    );
  }

  const generate = () => {
    if (!markdown.trim()) return message.error(t("privacy_policy_empty_error"));
    setPreview(markdown);
    setConfirmed(false);
    message.success(t("privacy_policy_generated_success"));
  };

  const save = async () => {
    if (!confirmed) return message.warning(t("privacy_policy_confirm_error"));

    setSaving(true);
    try {
      const created = await saveManagedPrivacyPolicy(preview);
      await publishManagedPrivacyPolicy(created.id!);
      setSavedMarkdown(preview);
      setConfirmed(false);
      message.success(t("privacy_policy_saved_success"));
    } catch {
      message.error(t("privacy_policy_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto p-6">
      <Title level={2}>{t("privacy_policy_title")}</Title>
      <Text type="secondary">{t("privacy_policy_description")}</Text>

      {error && <Alert className="my-4" type="error" message={error} />}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title={t("privacy_policy_markdown")}>
          <Input.TextArea
            value={markdown}
            onChange={(event) => setMarkdown(event.target.value)}
            autoSize={{ minRows: 24 }}
          />
          <div className="mt-4 flex justify-end">
            <Button type="primary" onClick={generate}>
              {t("privacy_policy_generate_preview")}
            </Button>
          </div>
        </Card>

        <Card title={t("privacy_policy_preview")}>
          {preview.trim() ? (
            <>
              <PrivacyPolicyPreview markdown={preview} showIndex />

              <div className="privacy-policy-divider mt-10 border-t pt-6">
                <div className="privacy-policy-confirmation rounded-lg p-4">
                  <Tooltip title={confirmationTooltip}>
                    <span>
                      <Checkbox
                        checked={confirmed}
                        disabled={!hasUnsavedChanges || markdown !== preview}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      >
                        {t("privacy_policy_confirm_preview")}
                      </Checkbox>
                    </span>
                  </Tooltip>
                  <div className="mt-5">
                    <Button
                      type="primary"
                      size="large"
                      className="min-w-40"
                      disabled={!canSave}
                      loading={saving}
                      onClick={save}
                    >
                      {t("privacy_policy_save")}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </Card>
      </div>
    </main>
  );
};

export default PrivacyPolicyForm;
